import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSystemCatalog,
  fetchOrgCatalogItems,
  fetchLocationCatalog,
  fetchLocationCatalogItems,
  upsertLocationCatalogItem,
  deleteLocationCatalogItem,
  fetchCatalogOverrides,
  upsertCatalogOverride,
  fetchCatalogGroups,
  createCatalogGroup,
  updateCatalogGroup,
  deleteCatalogGroup,
  createOrgCatalogItem,
  updateOrgCatalogItem,
  deleteOrgCatalogItem,
  fetchPricingCategories,
  createPricingCategory,
  updatePricingCategory,
  deletePricingCategory,
  fetchPricingPolicies,
  fetchPricingPolicy,
  createPricingPolicy,
  updatePricingPolicy,
  archivePricingPolicy,
  fetchPolicyVersions,
  createPolicyVersion,
  activatePolicyVersion,
  fetchPricingRates,
  upsertPricingRate,
  deletePricingRate,
  fetchLocationAssignments,
  createLocationAssignment,
  updateLocationAssignment,
  deleteLocationAssignment,
  resolvePricingRate,
} from '@/lib/api/pricing'
import { mapServiceCatalogRow, mapCatalogOverrideRow, mapCatalogGroupRow, mapPricingCategoryRow, mapPricingPolicyRow, mapPolicyVersionRow, mapPricingRateRow, type ResolvedCatalogItem, type ServiceType, type UnitOfMeasure, type FuelType } from '@/types/pricing'
import { useTenant } from '@/contexts'
import { toast } from 'sonner'

// --------------------------------------------------------------------------
// Query key factory
// --------------------------------------------------------------------------

export const pricingKeys = {
  all: ['pricing'] as const,
  systemCatalog: () => [...pricingKeys.all, 'systemCatalog'] as const,
  orgCatalog: () => [...pricingKeys.all, 'orgCatalog'] as const,
  locationCatalog: (locationId: string) => [...pricingKeys.all, 'locationCatalog', locationId] as const,
  locationCatalogItems: (locationId: string) => [...pricingKeys.all, 'locationCatalogItems', locationId] as const,
  overrides: () => [...pricingKeys.all, 'overrides'] as const,
  catalogGroups: () => [...pricingKeys.all, 'catalogGroups'] as const,
  categories: () => [...pricingKeys.all, 'categories'] as const,
  policies: () => [...pricingKeys.all, 'policies'] as const,
  policy: (id: string) => [...pricingKeys.all, 'policy', id] as const,
  versions: (policyId: string) => [...pricingKeys.all, 'versions', policyId] as const,
  rates: (versionId: string) => [...pricingKeys.all, 'rates', versionId] as const,
  assignments: (locationId: string) => [...pricingKeys.all, 'assignments', locationId] as const,
  resolution: (params: Record<string, unknown>) => [...pricingKeys.all, 'resolution', params] as const,
}

// --------------------------------------------------------------------------
// Catalog hooks
// --------------------------------------------------------------------------

export function useSystemCatalog() {
  return useQuery({
    queryKey: pricingKeys.systemCatalog(),
    queryFn: async () => {
      const rows = await fetchSystemCatalog()
      return rows.map(mapServiceCatalogRow)
    },
  })
}

export function useOrgCatalog() {
  return useQuery({
    queryKey: pricingKeys.orgCatalog(),
    queryFn: async () => {
      const rows = await fetchOrgCatalogItems()
      return rows.map(mapServiceCatalogRow)
    },
  })
}

export function useLocationCatalog(locationId: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.locationCatalog(locationId ?? ''),
    queryFn: async () => {
      const rows = await fetchLocationCatalog(locationId!)
      return rows.map((row: Record<string, unknown>) => ({
        catalogItemId: row.catalog_item_id as string,
        code: row.code as string,
        displayName: row.display_name as string,
        description: row.description as string | null,
        type: row.type as ServiceType,
        unitOfMeasure: row.unit_of_measure as UnitOfMeasure,
        fuelType: row.fuel_type as FuelType | null,
        glCode: row.gl_code as string | null,
        revenueCategory: row.revenue_category as string | null,
        isSystemItem: row.is_system_item as boolean,
        isThirdParty: row.is_third_party as boolean,
        isInventoryItem: row.is_inventory_item as boolean,
        sortOrder: row.sort_order as number,
        groupId: (row.group_id as string) ?? null,
        groupCode: (row.group_code as string) ?? null,
        groupName: (row.group_name as string) ?? null,
        groupSortOrder: (row.group_sort_order as number) ?? 999,
      })) as ResolvedCatalogItem[]
    },
    enabled: !!locationId,
  })
}

export function useLocationCatalogItems(locationId: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.locationCatalogItems(locationId ?? ''),
    queryFn: () => fetchLocationCatalogItems(locationId!),
    enabled: !!locationId,
  })
}

export function useCatalogOverrides() {
  return useQuery({
    queryKey: pricingKeys.overrides(),
    queryFn: async () => {
      const rows = await fetchCatalogOverrides()
      return rows.map(mapCatalogOverrideRow)
    },
  })
}

// --------------------------------------------------------------------------
// Group hooks
// --------------------------------------------------------------------------

export function useCatalogGroups() {
  return useQuery({
    queryKey: pricingKeys.catalogGroups(),
    queryFn: async () => {
      const rows = await fetchCatalogGroups()
      return rows.map(mapCatalogGroupRow)
    },
  })
}

// --------------------------------------------------------------------------
// Category hooks
// --------------------------------------------------------------------------

export function usePricingCategories() {
  return useQuery({
    queryKey: pricingKeys.categories(),
    queryFn: async () => {
      const rows = await fetchPricingCategories()
      return rows.map(mapPricingCategoryRow)
    },
  })
}

// --------------------------------------------------------------------------
// Policy hooks
// --------------------------------------------------------------------------

export function usePricingPolicies() {
  return useQuery({
    queryKey: pricingKeys.policies(),
    queryFn: async () => {
      const rows = await fetchPricingPolicies()
      return rows.map(mapPricingPolicyRow)
    },
  })
}

export function usePricingPolicy(id: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.policy(id ?? ''),
    queryFn: async () => {
      const row = await fetchPricingPolicy(id!)
      return mapPricingPolicyRow(row)
    },
    enabled: !!id,
  })
}

export function usePolicyVersions(policyId: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.versions(policyId ?? ''),
    queryFn: async () => {
      const rows = await fetchPolicyVersions(policyId!)
      return rows.map(mapPolicyVersionRow)
    },
    enabled: !!policyId,
  })
}

export function usePricingRates(versionId: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.rates(versionId ?? ''),
    queryFn: async () => {
      const rows = await fetchPricingRates(versionId!)
      return rows.map(mapPricingRateRow)
    },
    enabled: !!versionId,
  })
}

export function useLocationAssignments(locationId: string | undefined) {
  return useQuery({
    queryKey: pricingKeys.assignments(locationId ?? ''),
    queryFn: () => fetchLocationAssignments(locationId!),
    enabled: !!locationId,
  })
}

// --------------------------------------------------------------------------
// Resolution hook
// --------------------------------------------------------------------------

export function usePricingResolution(params: {
  locationId: string
  catalogItemCode: string
  aircraftId?: string
  clientId?: string
  serviceDate?: string
} | null) {
  return useQuery({
    queryKey: pricingKeys.resolution(params as Record<string, unknown>),
    queryFn: () => resolvePricingRate(params!),
    enabled: !!params,
  })
}

// --------------------------------------------------------------------------
// Mutation hooks
// --------------------------------------------------------------------------

export function usePricingMutations() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  const invalidateCatalog = () => {
    queryClient.invalidateQueries({ queryKey: pricingKeys.systemCatalog() })
    queryClient.invalidateQueries({ queryKey: pricingKeys.orgCatalog() })
  }

  const createCatalogItem = useMutation({
    mutationFn: async (input: {
      code: string
      name: string
      description?: string
      type: string
      unitOfMeasure: string
      fuelType?: string
      glCode?: string
      revenueCategory?: string
      isThirdParty?: boolean
      isInventoryItem?: boolean
      groupId?: string
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return createOrgCatalogItem({
        organization_id: currentOrganization.id,
        code: input.code,
        name: input.name,
        description: input.description || null,
        type: input.type,
        unit_of_measure: input.unitOfMeasure,
        fuel_type: input.fuelType || null,
        default_gl_code: input.glCode || null,
        default_revenue_category: input.revenueCategory || null,
        is_third_party: input.isThirdParty ?? false,
        is_inventory_item: input.isInventoryItem ?? false,
        is_system_item: false,
        group_id: input.groupId || null,
      })
    },
    onSuccess: () => {
      invalidateCatalog()
      toast.success('Service item created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create service item', { description: error.message })
    },
  })

  const updateCatalogItem = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      return updateOrgCatalogItem(id, data)
    },
    onSuccess: () => {
      invalidateCatalog()
      toast.success('Service item updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to update service item', { description: error.message })
    },
  })

  const removeCatalogItem = useMutation({
    mutationFn: deleteOrgCatalogItem,
    onSuccess: () => {
      invalidateCatalog()
      toast.success('Service item removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove service item', { description: error.message })
    },
  })

  const saveCatalogOverride = useMutation({
    mutationFn: async (input: {
      catalogItemId: string
      displayName?: string
      description?: string
      glCode?: string
      revenueCategory?: string
      sortOrder?: number
      isActive?: boolean
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return upsertCatalogOverride({
        organization_id: currentOrganization.id,
        catalog_item_id: input.catalogItemId,
        display_name: input.displayName || null,
        description: input.description || null,
        gl_code: input.glCode || null,
        revenue_category: input.revenueCategory || null,
        sort_order: input.sortOrder ?? null,
        is_active: input.isActive ?? true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.overrides() })
      invalidateCatalog()
      toast.success('Override saved')
    },
    onError: (error: Error) => {
      toast.error('Failed to save override', { description: error.message })
    },
  })

  // Categories
  const createCategory = useMutation({
    mutationFn: async (input: {
      code: string
      name: string
      description?: string
      sortOrder?: number
      matchBaseCategory?: string
      matchEngineCount?: number
      matchMinWeight?: number
      matchMaxWeight?: number
      matchFuelType?: string
      icon?: string
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return createPricingCategory({
        organization_id: currentOrganization.id,
        code: input.code,
        name: input.name,
        description: input.description || null,
        sort_order: input.sortOrder ?? 0,
        match_base_category: input.matchBaseCategory || null,
        match_engine_count: input.matchEngineCount ?? null,
        match_min_weight: input.matchMinWeight ?? null,
        match_max_weight: input.matchMaxWeight ?? null,
        match_fuel_type: input.matchFuelType || null,
        icon: input.icon || null,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() })
      toast.success('Category created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create category', { description: error.message })
    },
  })

  const updateCategory = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      return updatePricingCategory(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() })
      toast.success('Category updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to update category', { description: error.message })
    },
  })

  const removeCategory = useMutation({
    mutationFn: deletePricingCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.categories() })
      toast.success('Category removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove category', { description: error.message })
    },
  })

  // Policies
  const createPolicy = useMutation({
    mutationFn: async (input: {
      name: string
      description?: string
      scope: string
      ownerLocationId?: string
      isTaxInclusive?: boolean
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return createPricingPolicy({
        organization_id: currentOrganization.id,
        name: input.name,
        description: input.description || null,
        scope: input.scope,
        owner_location_id: input.ownerLocationId || null,
        is_tax_inclusive: input.isTaxInclusive ?? true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.policies() })
      toast.success('Policy created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create policy', { description: error.message })
    },
  })

  const updatePolicy = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      return updatePricingPolicy(id, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.policies() })
      queryClient.invalidateQueries({ queryKey: pricingKeys.policy(variables.id) })
    },
    onError: (error: Error) => {
      toast.error('Failed to update policy', { description: error.message })
    },
  })

  const archivePolicy = useMutation({
    mutationFn: archivePricingPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.policies() })
      toast.success('Policy archived')
    },
    onError: (error: Error) => {
      toast.error('Failed to archive policy', { description: error.message })
    },
  })

  // Versions
  const createVersion = useMutation({
    mutationFn: async (input: {
      policyId: string
      versionNumber: number
      effectiveFrom: string
      effectiveTo?: string
      label?: string
      notes?: string
    }) => {
      return createPolicyVersion({
        policy_id: input.policyId,
        version_number: input.versionNumber,
        effective_from: input.effectiveFrom,
        effective_to: input.effectiveTo || null,
        label: input.label || null,
        notes: input.notes || null,
      })
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.versions(variables.policyId) })
      toast.success('Version created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create version', { description: error.message })
    },
  })

  const activateVersion = useMutation({
    mutationFn: async ({ versionId, policyId }: { versionId: string; policyId: string }) => {
      return activatePolicyVersion(versionId, policyId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.versions(variables.policyId) })
      queryClient.invalidateQueries({ queryKey: pricingKeys.policies() })
      toast.success('Version activated')
    },
    onError: (error: Error) => {
      toast.error('Failed to activate version', { description: error.message })
    },
  })

  // Rates
  const saveRate = useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      return upsertPricingRate(input)
    },
    onSuccess: () => {
      // Caller should invalidate specific version rates
      toast.success('Rate saved')
    },
    onError: (error: Error) => {
      toast.error('Failed to save rate', { description: error.message })
    },
  })

  const removeRate = useMutation({
    mutationFn: deletePricingRate,
    onSuccess: () => {
      toast.success('Rate removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove rate', { description: error.message })
    },
  })

  // Assignments
  const createAssignment = useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      return createLocationAssignment(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.all })
      toast.success('Policy assigned')
    },
    onError: (error: Error) => {
      toast.error('Failed to assign policy', { description: error.message })
    },
  })

  const updateAssignment = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      return updateLocationAssignment(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.all })
    },
    onError: (error: Error) => {
      toast.error('Failed to update assignment', { description: error.message })
    },
  })

  const removeAssignment = useMutation({
    mutationFn: deleteLocationAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.all })
      toast.success('Policy unassigned')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove assignment', { description: error.message })
    },
  })

  // Groups
  const createGroup = useMutation({
    mutationFn: async (input: {
      code: string
      name: string
      description?: string
      allowedTypes?: string[]
      icon?: string
      sortOrder?: number
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return createCatalogGroup({
        organization_id: currentOrganization.id,
        code: input.code,
        name: input.name,
        description: input.description || null,
        allowed_types: input.allowedTypes ?? null,
        icon: input.icon || null,
        sort_order: input.sortOrder ?? 0,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.catalogGroups() })
      toast.success('Group created')
    },
    onError: (error: Error) => {
      toast.error('Failed to create group', { description: error.message })
    },
  })

  const updateGroup = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      return updateCatalogGroup(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.catalogGroups() })
      toast.success('Group updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to update group', { description: error.message })
    },
  })

  const removeGroup = useMutation({
    mutationFn: deleteCatalogGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.catalogGroups() })
      toast.success('Group removed')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove group', { description: error.message })
    },
  })

  // Location catalog toggles
  const toggleCatalogAvailability = useMutation({
    mutationFn: async (input: {
      locationId: string
      catalogItemId: string
      isAvailable: boolean
    }) => {
      return upsertLocationCatalogItem({
        location_id: input.locationId,
        catalog_item_id: input.catalogItemId,
        is_available: input.isAvailable,
      })
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.locationCatalogItems(variables.locationId),
      })
      queryClient.invalidateQueries({
        queryKey: pricingKeys.locationCatalog(variables.locationId),
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to update availability', { description: error.message })
    },
  })

  const resetCatalogItem = useMutation({
    mutationFn: async ({ id, locationId }: { id: string; locationId: string }) => {
      await deleteLocationCatalogItem(id)
      return locationId
    },
    onSuccess: (locationId) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.locationCatalogItems(locationId),
      })
      queryClient.invalidateQueries({
        queryKey: pricingKeys.locationCatalog(locationId),
      })
      toast.success('Reset to default')
    },
    onError: (error: Error) => {
      toast.error('Failed to reset item', { description: error.message })
    },
  })

  return {
    // Catalog
    createCatalogItem,
    updateCatalogItem,
    removeCatalogItem,
    saveCatalogOverride,
    // Location catalog
    toggleCatalogAvailability,
    resetCatalogItem,
    // Groups
    createGroup,
    updateGroup,
    removeGroup,
    // Categories
    createCategory,
    updateCategory,
    removeCategory,
    // Policies
    createPolicy,
    updatePolicy,
    archivePolicy,
    // Versions
    createVersion,
    activateVersion,
    // Rates
    saveRate,
    removeRate,
    // Assignments
    createAssignment,
    updateAssignment,
    removeAssignment,
  }
}
